/**
 * `/currency/rates/:code` — one currency's rates, newest first, with the lock
 * and the two acts on a single day: set it by hand, or delete it so the day
 * before holds. Behind `currency.admin`.
 */
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import { CURRENCY_BASE, type ExchangeRateDto } from '@forsys/shared';
import { AsyncContent } from '../../components/AsyncContent';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { EntityDetail } from '../../components/EntityDetail';
import { EntityTable } from '../../components/EntityTable';
import { Timestamp } from '../../components/Timestamp';
import { useAsync } from '../../hooks/useAsync';
import { useListQuery } from '../../hooks/useListQuery';
import { useSubmit } from '../../hooks/useSubmit';
import { deleteRate, listRates, listSources, setLocked } from './api';
import { currencyName, sourceLabel } from './format';
import { RateDialog } from './RateDialog';

export default function RateHistoryPage() {
  const { t } = useTranslation('currency');
  const code = (useParams()['code'] ?? '').toUpperCase();

  const list = useListQuery({ defaultSort: 'date', defaultOrder: 'desc', defaultLimit: 50, resetKey: code });
  const rates = useAsync(() => listRates(code, list.query), [code, list.key]);
  const sources = useAsync(listSources, []);

  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState<ExchangeRateDto | null>(null);

  const lock = useSubmit(async (locked: boolean) => {
    const result = await setLocked(code, locked);
    rates.reload();
    return result;
  });
  const remove = useSubmit(async () => {
    if (!deleting) return undefined;
    const result = await deleteRate(code, deleting.date);
    setDeleting(null);
    rates.reload();
    return result;
  });

  const known = sources.data?.sources ?? [];

  return (
    <AsyncContent state={rates}>
      {({ currency, locked, rates: rows, page }) => (
        <EntityDetail
          backTo="/currency/rates"
          backLabel={t('rates.back')}
          title={currency}
          badges={locked && <Chip size="small" icon={<LockOutlinedIcon />} label={t('lock.locked')} variant="outlined" />}
          description={[currencyName(currency), locked ? t('lock.explain') : ''].filter(Boolean).join(' · ')}
          error={lock.error}
          actions={
            <>
              <Button
                variant="outlined"
                startIcon={locked ? <LockOpenOutlinedIcon /> : <LockOutlinedIcon />}
                disabled={lock.pending}
                onClick={() => void lock.submit(!locked)}
              >
                {locked ? t('lock.unlock') : t('lock.lock')}
              </Button>
              <Button variant="contained" startIcon={<EditOutlinedIcon />} disabled={!sources.data} onClick={() => setEditing(true)}>
                {t('rates.setRate')}
              </Button>
            </>
          }
        >
          <EntityTable<ExchangeRateDto>
            rows={rows}
            rowKey={(rate) => rate.date}
            list={list}
            page={page}
            empty={{
              icon: <CurrencyExchangeIcon fontSize="inherit" />,
              title: t('history.emptyTitle'),
              description: t('history.emptyDescription'),
            }}
            columns={[
              { field: 'date', label: t('field.date'), sortable: true, width: 140, render: (rate) => rate.date },
              {
                field: 'rate',
                label: t('field.rate'),
                align: 'right',
                render: (rate) => t('rates.perUnit', { currency, rate: rate.rate, base: CURRENCY_BASE }),
              },
              { field: 'source', label: t('field.source'), render: (rate) => sourceLabel(rate.source, known, t('source.manual')) },
              { field: 'updatedBy', label: t('field.updatedBy'), render: (rate) => rate.updatedBy ?? t('history.cli') },
              {
                field: 'updatedAt',
                label: t('field.updatedAt'),
                width: 150,
                render: (rate) => <Timestamp value={rate.updatedAt} />,
              },
            ]}
            actions={(rate) => [
              {
                key: 'delete',
                label: t('history.delete'),
                icon: <DeleteOutlinedIcon fontSize="small" />,
                tone: 'error',
                onClick: () => setDeleting(rate),
              },
            ]}
            actionsWidth={70}
          />

          <RateDialog
            open={editing}
            currency={currency}
            initialDate={sources.data?.defaultRange.to ?? ''}
            onClose={() => setEditing(false)}
            onSaved={() => rates.reload()}
          />
          <ConfirmDialog
            open={deleting !== null}
            tone="error"
            title={t('history.deleteDialog.title', { currency, date: deleting?.date ?? '' })}
            message={t('history.deleteDialog.message')}
            confirmLabel={t('history.delete')}
            pending={remove.pending}
            error={remove.error}
            onClose={() => setDeleting(null)}
            onConfirm={() => void remove.submit()}
          />
        </EntityDetail>
      )}
    </AsyncContent>
  );
}
